import { StyleSheet, Text, View } from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  FormComponent,
  FormStructure,
  IFormData,
} from '../../../components/FormHelper/FormHelper.types';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import DatePicker from '../../../components/DatePicker';
import { STYLE_VARS } from '../../../constants/style-vars';
import DiningDropDown from '../../../components/DiningDropDown';
import { VENUE_ITEMS } from '../../../constants/dropdown-items';
import { DropDownItem } from '../../../models/DropDownItem';
import { Button } from '../../../components/Button';
import { TextInput } from '../../../components/TextInput/TextInput';
import { convertToAmPm } from '../../../services/utils/timeConvert';
import { COLORS, FONTS } from '../../../constants';
import DropDown from '../../../components/DropDown/DropDown';
import { useSelector } from 'react-redux';
import { RootState } from '../../../services/redux';
import DiningApi from '../../../services/api/dining.api';
import { setHeaderConfigAxios } from '../../../services/api/axios';
import * as yup from 'yup';
import { IVenue } from '../../../types/dining.types';
import { Loading } from '../../../components';

type BookingPrivateFormProps = {
  onSubmitHandler?: (data: IFormData) => void;
};

const BookingPrivateForm: FC<BookingPrivateFormProps> = ({
  onSubmitHandler,
}) => {
  const initData = useMemo(() => {
    return {
      date: dayjs(new Date()).format('YYYY-MM-DD'),
      venue: '',
      time: '',
      nPeople: '',
      type: '',
      request: '',
    };
  }, []);
  const [venues, setVenues] = useState<IVenue[]>([]);
  const auth = useSelector((redux: RootState) => redux.authReducer);
  const [isLoadingVenues, setIsLoadingVenues] = useState<boolean>(false);

  const getVenues = useCallback(() => {
    setIsLoadingVenues(true);
    DiningApi.getVenue('private')
      .then((res) => {
        setVenues(res.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingVenues(false);
      });
  }, []);

  const numberSelect = useCallback((name: string) => {
    const arr: DropDownItem[] = [];
    for (let i = 1; i <= 100; i++) {
      arr.push({
        label: i + ' ' + name,
        value: i,
      });
    }
    return arr;
  }, []);

  const timeSequence = useMemo(() => {
    const result = [];
    const startTime = new Date(`2000-01-01T09:00:00`);
    const endTime = new Date(`2000-01-01T19:00:00`);

    let currentTime = startTime;
    while (currentTime <= endTime) {
      const hours = currentTime.getHours().toString().padStart(2, '0');
      const minutes = currentTime.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;
      result.push(formattedTime);
      currentTime.setMinutes(currentTime.getMinutes() + 60);
    }
    return result.map((time) => {
      return {
        label: convertToAmPm(time),
        value: time,
      };
    });
  }, []);

  const venueItems = useMemo(() => {
    return venues.map((item, index) => {
      return {
        label: item.venue[0],
        value: item.venue[0],
      };
    });
  }, [venues]);

  const bookTableStructure = useMemo(() => {
    const structure: FormStructure = {
      components: [
        {
          name: 'date',
          type: 'datePicker',
          label: 'Select Date',
          zIndex: 55,
        },
        {
          name: 'venue',
          type: 'dropdown',
          label: 'Select Venue',
          items: venueItems,
          zIndex: 54,
          titleDropdown: 'Please Select Venue',
        },
        {
          name: 'time',
          type: 'dropdown',
          label: 'Select Time',
          items: timeSequence,
          zIndex: 53,
          titleDropdown: 'Please Select Time',
        },
        {
          name: 'type',
          type: 'dropdown',
          label: 'Select Type',
          items: [
            { label: 'Meeting', value: 'Meeting' },
            { label: `Children's Party`, value: `Children's Party` },
            { label: 'Private Party', value: 'Private Party' },
            { label: 'Cocktails', value: 'Cocktails' },
            { label: 'Other', value: 'Other' },
          ],
          zIndex: 52,
          titleDropdown: 'Please Select Type',
        },
        {
          name: 'nPeople',
          type: 'dropdown',
          label: 'No. of People',
          items: numberSelect('adults'),
          zIndex: 51,
          titleDropdown: 'Please Select No. of People',
        },
      ],
    };
    return structure;
  }, [numberSelect, timeSequence, venueItems]);

  const renderForm = useCallback((component: FormComponent) => {
    switch (component.type) {
      case 'datePicker':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{}}>
                <DatePicker
                  date={field.value}
                  onChangeDate={(date) => {
                    field.onChange(field.name)(date);
                  }}
                  haveCalendar={true}
                />
                <ErrorMessage
                  name={field.name}
                  render={(errorMessage: string) => (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errorMessage}
                    </Text>
                  )}
                />
              </View>
            )}
          </Field>
        );
      case 'dropdown':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{}}>
                <DropDown
                  items={component.items || []}
                  placeholder={'SELECT'}
                  customDownButton={require('../../../assets/down-arrow.png')}
                  customUpButton={require('../../../assets/up-arrow.png')}
                  zIndex={component.zIndex}
                  dropdownStyle={{
                    borderColor: COLORS.DARK_GREY,
                  }}
                  textStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  dropDownContainerTextStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  buttonStyle={{
                    tintColor: COLORS.DARKER_GREY,
                  }}
                  setValues={(value) => {
                    field.onChange(field.name)(value.value.toString());
                  }}
                  title={component.titleDropdown}
                />
                <ErrorMessage
                  name={field.name}
                  render={(errorMessage: string) => (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errorMessage}
                    </Text>
                  )}
                />
              </View>
            )}
          </Field>
        );
      default:
        return <></>;
    }
  }, []);

  useEffect(() => {
    setHeaderConfigAxios(auth.token);
    getVenues();
  }, [getVenues, auth.token]);

  return (
    <View>
      {isLoadingVenues ? (
        <View
          style={{
            marginTop: 30,
            marginBottom: 30,
          }}
        >
          <Loading />
        </View>
      ) : (
        <Formik
          initialValues={initData}
          onSubmit={(value) => {
            if (onSubmitHandler) onSubmitHandler(value);
          }}
          validationSchema={yup.object().shape({
            date: yup.string().required('Please Select Date.'),
            venue: yup.string().required('Please Select Venue'),
            time: yup.string().required('Please Select Time'),
            type: yup.string().required('Please Select Type'),
            nPeople: yup.number().required('Please Select No. Of People'),
            request: yup.string(),
          })}
        >
          {({ handleSubmit, handleChange, values, errors }) => (
            <View
              style={{
                marginVertical: 20,
              }}
            >
              {bookTableStructure.components.map((component) => (
                <View
                  key={component.name}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                    zIndex: component.zIndex,
                  }}
                >
                  <View style={{ width: '32%' }}>
                    <Text
                      style={{
                        fontFamily: FONTS.IowanOldStyleBTRoman,
                        fontSize: 14,
                        textTransform: 'uppercase',
                        color: STYLE_VARS.textColor,
                      }}
                    >
                      {component.label}
                    </Text>
                  </View>
                  <View style={{ width: '65%' }}>{renderForm(component)}</View>
                </View>
              ))}
              <View style={{ marginBottom: 20 }}>
                <Text
                  style={{
                    color: STYLE_VARS.textColor,
                    textTransform: 'uppercase',
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                    fontSize: 13,
                    fontWeight: '500',
                    marginBottom: 10,
                  }}
                >
                  Special Requests
                </Text>
                <TextInput
                  placeholder={'Message'}
                  onChangeText={handleChange('request')}
                  value={values.request}
                  textAlignVertical="top"
                  multiline
                  placeholderTextColor={STYLE_VARS.fccDarkGrey}
                  style={{
                    height: 100,
                    paddingHorizontal: 10,
                    alignItems: 'flex-start',
                    paddingVertical: 10,
                    borderWidth: 0.5,
                    borderColor: STYLE_VARS.fccDarkGrey,
                    color: STYLE_VARS.fccDarkerGrey,
                    fontSize: 12,
                    letterSpacing: 0.8,
                    fontWeight: '500',
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                  }}
                />
                {errors.request && (
                  <Text style={{ color: 'red', marginTop: 5 }}>
                    {errors.request}
                  </Text>
                )}
              </View>
              <View style={{ marginBottom: 20 }}>
                <Text
                  style={{
                    color: STYLE_VARS.kccDanger,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    textAlign: 'justify',
                  }}
                >
                  NOTE: We will confirm your booking within 24 hours.
                </Text>
              </View>
              <View style={{ alignSelf: 'center', marginBottom: 20 }}>
                <Button
                  text="BOOK NOW"
                  style={STYLE_VARS.blueButton}
                  onPress={() => {
                    handleSubmit();
                  }}
                />
              </View>
              <View>
                <Text
                  style={{
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                    fontSize: 16,
                    color: COLORS.BLACK,
                  }}
                >
                  <Text
                    style={{
                      fontWeight: 'bold',
                      marginRight: 5,
                    }}
                  >
                    {'Cancellation Policy: '}
                  </Text>
                  {
                    '\nMembers can cancel their bookings within 24 hours of making the reservation. After 24 hours, please call to cancel. For special occasions, kindly provide 7-10 days’ notice for cancellation.'
                  }
                </Text>
              </View>
            </View>
          )}
        </Formik>
      )}
    </View>
  );
};

export default BookingPrivateForm;
